
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQEn = () => {
  const { t } = useTranslation(['common']);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "What is uplivion and how does it work?",
          answer: "uplivion is a platform for creating personal life timelines. You can organize your memories chronologically, add photos, and share with friends. The timeline is divided into life phases to help you organize."
        },
        {
          question: "Is uplivion free?",
          answer: "Yes, during the beta phase uplivion is completely free. You can use all basic features, add memories, and share with friends. In the future, we will offer premium features for advanced functionality."
        },
        {
          question: "How do I create my first timeline?",
          answer: "After registration, a timeline is automatically created for you. It's already divided into life phases. Click on a phase (e.g., 'Childhood') to expand it and add your first memories."
        }
      ]
    },
    {
      category: "Memories & Content",
      questions: [
        {
          question: "What types of content can I add?",
          answer: "You can add texts, photos, and event details. Each memory can contain a title, description, date, and multiple photos. You can also add tags to categorize your memories."
        },
        {
          question: "How many photos can I upload?",
          answer: "You can upload multiple photos per memory. Each photo can be up to 10MB in size. Supported formats are JPG, PNG, and WEBP. During beta phase, there are generous storage limits."
        },
        {
          question: "Can I edit or delete memories later?",
          answer: "Yes, you can edit or delete all your memories at any time. Simply click on a memory to open it and use the edit or delete options."
        }
      ]
    },
    {
      category: "Friends & Sharing",
      questions: [
        {
          question: "How do I find and add friends?",
          answer: "Go to the Friends section and search by username or email address. You can send and receive friend requests. Alternatively, friends can search for you too."
        },
        {
          question: "Who can see my memories?",
          answer: "You decide! Each memory has privacy settings: 'Only me' (private), 'Friends' (all your friends), or 'Public' (coming soon). You have full control over visibility."
        },
        {
          question: "Can friends add memories to my timeline?",
          answer: "Yes, friends can add memories to your timeline, which you then need to approve. This is perfect for shared experiences or when friends have photos of you."
        }
      ]
    },
    {
      category: "Privacy & Security",
      questions: [
        {
          question: "Is my data safe?",
          answer: "Yes, privacy is our top priority. All data is transmitted and stored encrypted. We follow GDPR and only store data on EU servers. Your data belongs to you."
        },
        {
          question: "Who can access my data?",
          answer: "Only you have access to your private data. uplivion staff cannot access your personal content. Technical data is only used for improvements and support."
        },
        {
          question: "Can I export or delete my data?",
          answer: "Yes, you always have the right to export your data or completely delete your account. Export functions are in development; contact us for manual export if needed."
        }
      ]
    },
    {
      category: "Beta Phase",
      questions: [
        {
          question: "What does 'Beta Phase' mean?",
          answer: "Beta phase means uplivion is still in development. You can use all core features for free, but there may occasionally be updates, changes, or minor issues. Your feedback helps us improve."
        },
        {
          question: "Will I lose my data during beta?",
          answer: "No, your data is safe. We make regular backups and migrate data during updates. In very rare cases there might be technical issues, but we work hard to prevent data loss."
        },
        {
          question: "How can I give feedback?",
          answer: "We welcome your feedback! Write to us at support@uplivion.life or use the feedback function in the app. Your ideas and suggestions directly influence development."
        }
      ]
    },
    {
      category: "Technical Questions",
      questions: [
        {
          question: "Which browsers are supported?",
          answer: "uplivion works best with modern browsers like Chrome, Firefox, Safari, and Edge. We recommend always using the latest version for the best experience."
        },
        {
          question: "Is there a mobile app?",
          answer: "A native mobile app for iOS and Android is planned. Currently, you can use uplivion through your mobile browser - the website is optimized for mobile devices."
        },
        {
          question: "Why is my timeline loading slowly?",
          answer: "With many memories containing photos, loading can take some time. We're continuously working on performance. Also check your internet connection and occasionally clear your browser cache."
        }
      ]
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Back to Home
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions (FAQ)</h1>
            <p className="text-xl text-gray-600">Answers to the most common questions about uplivion.</p>
          </div>

          <div className="mb-8">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-8">
                <h2 className="text-2xl font-semibold text-uplivion-blue mb-4 border-b pb-2">
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((faq, questionIndex) => {
                    const globalIndex = categoryIndex * 10 + questionIndex;
                    return (
                      <div 
                        key={globalIndex} 
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFAQ(globalIndex)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900">{faq.question}</span>
                          {openFAQ === globalIndex ? (
                            <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                          )}
                        </button>
                        {openFAQ === globalIndex && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-uplivion-blue/5 to-uplivion-blue/10 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-3">Your question isn't here?</h2>
            <p className="text-gray-600 mb-4">
              No problem! Our support team is happy to help. As a beta user, you receive 
              priority support and direct contact to our development team.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild>
                <Link to="/contact-en">Contact Us</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/help-en">Visit Help Center</Link>
              </Button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h3 className="font-semibold text-yellow-800 mb-2">Beta Support</h3>
            <p className="text-yellow-700 text-sm">
              As a beta user, you have access to our premium support. We typically respond 
              within 24 hours and take your feedback seriously. Your experiences help us make 
              uplivion the best platform for personal memories.
            </p>
          </div>

          <div className="pt-8 border-t">
            <Button asChild>
              <Link to="/">Back to Home</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQEn;
